import {Controller} from "@hotwired/stimulus"

// Connects to data-controller="recurring-select"
export default class extends Controller {
  static targets = [
    "patternSelect", "patternInput",
    "customPanel",
    "interval", "frequency",
    "weeklyOptions", "dayOfWeek",
    "monthlyOptions", "monthlyMode",
    "dayOfMonthOptions", "dayOfMonth",
    "dayOfWeekInMonthOptions", "monthlyOrdinal", "monthlyDayOfWeek",
    "yearlyOptions", "monthOfYear", "yearlyDayOfMonth"
  ]

  connect() {
    const currentValue = this.patternInputTarget.value
    if (!currentValue) {
      this.patternInputTarget.value = this.patternSelectTarget.value
      return
    }

    const presetOption = [...this.patternSelectTarget.options].find(opt => opt.value === currentValue)
    if (presetOption) {
      this.patternSelectTarget.value = currentValue
    } else {
      this.patternSelectTarget.value = "custom"
      this.showCustomPanel()
      this.populateFromRule(JSON.parse(currentValue))
    }
  }

  handlePatternChange() {
    const value = this.patternSelectTarget.value
    if (value === "custom") {
      this.showCustomPanel()
      this.buildRule()
    } else {
      this.hideCustomPanel()
      this.patternInputTarget.value = value
    }
  }

  handleFrequencyChange() {
    this.showFrequencyOptions()
    this.buildRule()
  }

  handleMonthlyModeChange() {
    this.showMonthlyModeOptions()
    this.buildRule()
  }

  // Generates IceCube's native hash format, matching what IceCube::Rule#to_hash produces.
  buildRule() {
    const freq = this.frequencyTarget.value
    const interval = parseInt(this.intervalTarget.value) || 1
    const ruleTypeMap = {
      weekly: "IceCube::WeeklyRule",
      monthly: "IceCube::MonthlyRule",
      yearly: "IceCube::YearlyRule"
    }
    const validations = {}

    if (freq === "weekly") {
      const days = this.dayOfWeekTargets
        .filter(cb => cb.checked)
        .map(cb => parseInt(cb.value))
      if (days.length > 0) validations.day = days

    } else if (freq === "monthly") {
      if (this.monthlyModeTarget.value === "day_of_month") {
        const days = this.dayOfMonthTargets
          .filter(cb => cb.checked)
          .map(cb => parseInt(cb.value))
        if (days.length > 0) validations.day_of_month = days
      } else {
        const ordinal = parseInt(this.monthlyOrdinalTarget.value)
        const dayNum = parseInt(this.monthlyDayOfWeekTarget.value)
        validations.day_of_week = {[dayNum]: [ordinal]}
      }

    } else if (freq === "yearly") {
      const months = this.monthOfYearTargets
        .filter(cb => cb.checked)
        .map(cb => parseInt(cb.value))
      if (months.length > 0) validations.month_of_year = months

      const days = this.yearlyDayOfMonthTargets
        .filter(cb => cb.checked)
        .map(cb => parseInt(cb.value))
      if (days.length > 0) validations.day_of_month = days
    }

    const rule = {rule_type: ruleTypeMap[freq], interval, validations}
    if (freq === "weekly") rule.week_start = 0

    this.patternInputTarget.value = JSON.stringify(rule)
  }

  // Restores controls from a stored IceCube rule hash.
  // Handles both IceCube's native format {rule_type, interval, validations}
  // and the transitional format {weekly/monthly/yearly: {interval, validations}}.
  populateFromRule(ruleHash) {
    const {freq, interval, validations} = this.extractRuleParts(ruleHash)

    this.frequencyTarget.value = freq
    this.intervalTarget.value = interval
    this.showFrequencyOptions()

    if (freq === "weekly") {
      if (validations.day) {
        this.dayOfWeekTargets.forEach(cb => {
          cb.checked = validations.day.includes(parseInt(cb.value))
        })
      }

    } else if (freq === "monthly") {
      if (validations.day_of_month) {
        this.monthlyModeTarget.value = "day_of_month"
        this.showMonthlyModeOptions()
        this.dayOfMonthTargets.forEach(cb => {
          cb.checked = validations.day_of_month.includes(parseInt(cb.value))
        })
      } else if (validations.day_of_week) {
        this.monthlyModeTarget.value = "day_of_week"
        this.showMonthlyModeOptions()
        const dayNum = Object.keys(validations.day_of_week)[0]
        this.monthlyDayOfWeekTarget.value = dayNum
        this.monthlyOrdinalTarget.value = validations.day_of_week[dayNum][0]
      }

    } else if (freq === "yearly") {
      if (validations.month_of_year) {
        this.monthOfYearTargets.forEach(cb => {
          cb.checked = validations.month_of_year.includes(parseInt(cb.value))
        })
      }
      if (validations.day_of_month) {
        this.yearlyDayOfMonthTargets.forEach(cb => {
          cb.checked = validations.day_of_month.includes(parseInt(cb.value))
        })
      }
    }
  }

  // Extracts freq/interval/validations from IceCube's native hash format:
  // {rule_type: "IceCube::WeeklyRule", interval: 1, week_start: 0, validations: {...}}
  extractRuleParts(ruleHash) {
    const rt = ruleHash.rule_type || ""
    let freq = "weekly"
    if (rt.includes("Monthly")) freq = "monthly"
    else if (rt.includes("Yearly")) freq = "yearly"
    else if (rt.includes("Daily")) freq = "daily"
    return {freq, interval: ruleHash.interval || 1, validations: ruleHash.validations || {}}
  }

  showCustomPanel() {
    this.customPanelTarget.classList.remove("hidden")
    this.showFrequencyOptions()
  }

  hideCustomPanel() {
    this.customPanelTarget.classList.add("hidden")
  }

  showFrequencyOptions() {
    const freq = this.frequencyTarget.value
    this.weeklyOptionsTarget.classList.toggle("hidden", freq !== "weekly")
    this.monthlyOptionsTarget.classList.toggle("hidden", freq !== "monthly")
    this.yearlyOptionsTarget.classList.toggle("hidden", freq !== "yearly")
    if (freq === "monthly") this.showMonthlyModeOptions()
  }

  showMonthlyModeOptions() {
    const mode = this.monthlyModeTarget.value
    this.dayOfMonthOptionsTarget.classList.toggle("hidden", mode !== "day_of_month")
    this.dayOfWeekInMonthOptionsTarget.classList.toggle("hidden", mode !== "day_of_week")
  }
}
